import type { TExample } from '@atlas/uicorn/preview/modules';
import React from 'react';
import styled, { keyframes } from 'styled-components';

const Spinner = React.memo(function Spinner(props: TSpinnerProps): React.ReactElement {
    const { horizontalFit, verticalFit, small, ...elementProps } = props;
    const { width, height, cx, cy, r, strokeWidth, dashKeyframes } = small ? smallSizes : normalSizes;

    if (horizontalFit || verticalFit) {
        return (
            <SSpinnerContainer $verticalFit={verticalFit} $horizontalFit={horizontalFit}>
                <SSpinner
                    {...elementProps}
                    $strokeWidth={strokeWidth}
                    width={width}
                    height={height}
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <SSpinnerCircle cx={cx} cy={cy} r={r} $dashKeyframes={dashKeyframes} />
                </SSpinner>
            </SSpinnerContainer>
        );
    }

    return (
        <SSpinner
            {...elementProps}
            $strokeWidth={strokeWidth}
            width={width}
            height={height}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
        >
            <SSpinnerCircle cx={cx} cy={cy} r={r} $dashKeyframes={dashKeyframes} />
        </SSpinner>
    );
});

export default Spinner;

// #region Styles

const rotateKeyframes = keyframes`
	0% {transform: rotate(0deg); }
	100% {transform: rotate(360deg); }
`;

const SSpinnerCircle = styled.circle<{ $dashKeyframes: ReturnType<typeof keyframes> }>`
    transform-origin: center;
    animation: ${({ $dashKeyframes }) => $dashKeyframes} 2s ease infinite, ${rotateKeyframes} 2s linear infinite;
`;

const SSpinner = styled.svg<{ $strokeWidth: number }>`
    fill: transparent;
    /*stroke: #49BDE4;*/
    stroke-width: ${({ $strokeWidth }) => $strokeWidth};
`;

const SSpinnerContainer = styled.div<{ $horizontalFit?: boolean; $verticalFit?: boolean }>`
    display: flex;

    ${({ $horizontalFit }) => $horizontalFit && 'width: 100%; justify-content: center;'};
    ${({ $verticalFit }) => $verticalFit && 'height: 100%; align-items: center;'};
`;

// #endregion

// #region Types

export type TSpinnerProps = Omit<React.SVGAttributes<SVGElement>, 'children'> & {
    small?: boolean;
    horizontalFit?: boolean;
    verticalFit?: boolean;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render({ small }) {
        return <Spinner small={small} />;
    },
    controls: {
        small: { type: 'boolean', initial: false },
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?node-id=1462:5918&mode=dev',
};

export const AlignedSpinnerExample: TExample = {
    containerStyle: {
        width: '40vh',
        height: '40vh',
        border: '1px dashed green',
        padding: 0,
        display: 'block',
    },
    render({ horizontalFit, verticalFit }) {
        return <Spinner verticalFit={verticalFit} horizontalFit={horizontalFit} />;
    },
    controls: {
        verticalFit: { type: 'boolean', initial: true },
        horizontalFit: { type: 'boolean', initial: true },
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?node-id=1462:5918&mode=dev',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Spinner', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion

// #region Helpers

const smallSizes = {
    width: 14,
    height: 14,
    cx: 7,
    cy: 7,
    r: 5,
    strokeWidth: 2,
    dashKeyframes: keyframes`
        0% {
            stroke-dasharray: 7,42;
            stroke-dashoffset: 0;
            stroke: #0000FF;
        }
        50% {
            stroke-dasharray: 21,8;
            stroke-dashoffset: -4;
            stroke: #A056FF;
        }
        100% {
            stroke-dasharray: 21,42;
            stroke-dashoffset: -35;
            stroke: #0000FF;
        }
    `,
};

const normalSizes = {
    width: 22,
    height: 22,
    cx: 11,
    cy: 11,
    r: 9,
    strokeWidth: 3,
    dashKeyframes: keyframes`
        0% {
            stroke-dasharray: 10,80;
            stroke-dashoffset: 0;
            stroke: #0000FF;
        }
        50% {
            stroke-dasharray: 45,20;
            stroke-dashoffset: -15;
            stroke: #A056FF;
        }
        100% {
            stroke-dasharray: 45,95;
            stroke-dashoffset: -100;
            stroke: #0000FF;
        }
    `,
};

// #endregion
